import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Plus, RefreshCw } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { FilterBar, FilterSearch, FilterSelect } from "@/components/data-display/filter-bar";
import { StatCard } from "@/components/data-display/stat-card";
import { DataSourceBadge } from "@/components/data-display/data-source-badge";
import { PermissionGate } from "@/components/permission/permission-gate";
import { TransferRow } from "@/features/transfers/transfer-table";
import { useTransfers } from "@/hooks/use-transfers";
import { cn } from "@/lib/utils";

const STATUSES = [
  "All Status",
  "Reconciled",
  "Pending Approval",
  "Pending Screening",
  "Signing Authorized",
  "Failed",
  "Rejected",
];

const ASSETS = ["All Assets", "ETH", "USDC", "USDT", "BTC", "MATIC"];

export default function TransfersPage() {
  const { transfers, stats, loading, error, source, refresh } = useTransfers();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(STATUSES[0]);
  const [wallet, setWallet] = useState("All Wallets");
  const [asset, setAsset] = useState(ASSETS[0]);

  const walletOptions = useMemo(() => {
    const names = [...new Set(transfers.map(t => t.route.split(" → ")[0]).filter(Boolean))];
    return ["All Wallets", ...names];
  }, [transfers]);

  const rows = useMemo(() => {
    return transfers.filter((t) => {
      const q = query.toLowerCase();
      const matchesQ = !q || `${t.id} ${t.route} ${t.asset} ${t.amount} ${t.creator}`.toLowerCase().includes(q);
      const matchesStatus = status === STATUSES[0] || t.statusLabel === status;
      const matchesWallet = wallet === "All Wallets" || t.route.includes(wallet);
      const matchesAsset = asset === ASSETS[0] || t.asset === asset;
      return matchesQ && matchesStatus && matchesWallet && matchesAsset;
    });
  }, [transfers, query, status, wallet, asset]);

  return (
    <div>
      <div className="mb-5 flex items-center gap-2">
        <DataSourceBadge source={source} />
      </div>

      {!loading && !error && (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {stats.map((s) => (
            <StatCard key={s.label} {...s} />
          ))}
        </div>
      )}

      <FilterBar className="mb-5">
        <FilterSearch value={query} onChange={setQuery} placeholder="Search by ID, wallet, address, amount…" />
        <FilterSelect value={status} onChange={setStatus} options={STATUSES.map((s) => ({ value: s, label: s }))} ariaLabel="Filter by status" />
        <FilterSelect value={wallet} onChange={setWallet} options={walletOptions.map((w) => ({ value: w, label: w }))} ariaLabel="Filter by wallet" />
        <FilterSelect value={asset} onChange={setAsset} options={ASSETS.map((a) => ({ value: a, label: a }))} ariaLabel="Filter by asset" />
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-muted hover:text-ink"
          aria-label="Refresh transfers"
        >
          <RefreshCw className="size-3.5" aria-hidden />
        </button>
        <PermissionGate permission="transfer:create">
          <Link to="/transfers/new" className={cn(buttonVariants({ size: "sm" }), "ml-auto")}>
            <Plus className="size-4" aria-hidden /> Create Intent
          </Link>
        </PermissionGate>
      </FilterBar>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="spinner" aria-label="Loading transfers" />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-danger-border bg-danger-soft px-4 py-6 text-center text-sm text-danger">
          Failed to load transfers: {error}
          <button type="button" onClick={refresh} className="ml-2 underline">Retry</button>
        </div>
      )}

      {!loading && !error && <TableWrapper>
        <Table className="dense">
          <colgroup>
            <col style={{ width: 92 }} />
            <col style={{ width: 88 }} />
            <col style={{ width: 190 }} />
            <col style={{ width: 64 }} />
            <col style={{ width: 92 }} />
            <col style={{ width: 148 }} />
            <col style={{ width: 76 }} />
            <col style={{ width: 124 }} />
            <col style={{ width: 44 }} />
          </colgroup>
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className="px-4 py-2.5">Intent ID</th>
              <th className="px-4 py-2.5">Direction</th>
              <th className="px-4 py-2.5">From / To</th>
              <th className="px-4 py-2.5">Asset</th>
              <th className="px-4 py-2.5">Amount</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Created</th>
              <th className="px-4 py-2.5">Creator</th>
              <th className="px-4 py-2.5 w-10" aria-label="Row actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <TransferRow key={t.id} transfer={t} href={`/transfers/${t.id}?state=${t.state}`} />
            ))}
          </tbody>
        </Table>
        {rows.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted">No transfers match your filters.</div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3">
          <span className="text-xs text-muted">Showing {rows.length} of {transfers.length} transfers</span>
          <nav aria-label="Pagination" className="flex items-center gap-1 text-xs">
            <span aria-disabled className="rounded px-2 py-1 font-medium text-subtle">← Prev</span>
            <span aria-current="page" className="rounded bg-primary-soft px-2 py-1 font-semibold text-primary">1</span>
            <button type="button" className="rounded px-2 py-1 font-medium text-muted transition-colors hover:text-ink">2</button>
            <button type="button" className="rounded px-2 py-1 font-medium text-muted transition-colors hover:text-ink">3</button>
            <button type="button" className="rounded px-2 py-1 font-medium text-muted transition-colors hover:text-ink">Next →</button>
          </nav>
        </div>
      </TableWrapper>}
    </div>
  );
}
